import { api, unwrap } from "@lumberset/api-client";
import { date } from "@lumberset/takeoff-ui";
import {
  Button,
  Container,
  Field,
  Hint,
  IconButton,
  Input,
  SectionHeader,
  Select,
  Textarea,
  toast,
} from "@lumberset/ui";
import { PaperPlane, Plus, Trash } from "@medusajs/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { type FormEvent, useState } from "react";
import { Crumbs } from "../../../components/crumbs";
import { projectQuery, projectsQuery, takeoffQuery } from "../../../lib/queries";
import {
  looksLikeEmail,
  MAX_RECIPIENTS,
  type RecipientDraft,
  supplierDetail,
  supplierName,
} from "../../../lib/rfq";

/** `?runId=` arrives from a takeoff screen with that run already chosen. */
export const Route = createFileRoute("/_app/rfqs/new")({
  validateSearch: (search: Record<string, unknown>): { runId?: string } =>
    typeof search.runId === "string" && search.runId ? { runId: search.runId } : {},
  component: NewRfq,
});

function NewRfq() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const search = Route.useSearch();

  const [chosenProject, setChosenProject] = useState<string | null>(null);
  const [chosenRun, setChosenRun] = useState<string | null>(null);
  const [message, setMessage] = useState("");
  const [recipients, setRecipients] = useState<RecipientDraft[]>([]);

  const { data: projects } = useQuery(projectsQuery());
  // The run named in the URL tells us which project to start on.
  const { data: given } = useQuery({
    ...takeoffQuery(search.runId ?? ""),
    enabled: Boolean(search.runId),
  });
  const projectId = chosenProject ?? given?.run.projectId ?? "";
  const { data: detail } = useQuery({ ...projectQuery(projectId), enabled: Boolean(projectId) });
  const runId = chosenRun ?? search.runId ?? "";

  const released = detail?.runs.filter((run) => run.status === "released") ?? [];
  const runLabel = (run: (typeof released)[number]) =>
    `${detail?.planSets.find((set) => set.id === run.planSetId)?.label ?? "Drawings"}, ready ${date(run.releasedAt)}`;

  const create = useMutation({
    mutationFn: async (send: boolean) => {
      const created = await unwrap(
        api.rfqs.$post({
          json: {
            runId,
            message: message.trim() || null,
            recipients: recipients.map((recipient) => ({
              email: recipient.email,
              name: recipient.name || null,
              company: recipient.company || null,
            })),
          },
        }),
      );
      if (!send) return { id: created.rfq.id, sent: 0, failed: 0 };
      const result = await unwrap(api.rfqs[":id"].send.$post({ param: { id: created.rfq.id } }));
      return { id: created.rfq.id, sent: result.sent, failed: result.failed };
    },
    onSuccess: async (result, send) => {
      await queryClient.invalidateQueries({ queryKey: ["rfqs"] });
      if (!send) toast.success("RFQ drafted", { description: "Send it when the list is ready." });
      else if (result.failed > 0) {
        toast.error(
          `${result.failed} ${result.failed === 1 ? "email" : "emails"} could not be sent`,
          {
            description: `${result.sent} sent. Send again to retry the rest.`,
          },
        );
      } else {
        toast.success(`RFQ sent to ${result.sent} ${result.sent === 1 ? "yard" : "yards"}`, {
          description: "You will hear here as their quotes come in.",
        });
      }
      await navigate({ to: "/rfqs/$id", params: { id: result.id } });
    },
    onError: (error) => toast.error(error.message),
  });

  function addRecipient(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    const email = String(data.get("email") ?? "")
      .trim()
      .toLowerCase();
    const name = String(data.get("name") ?? "").trim();
    const company = String(data.get("company") ?? "").trim();

    if (!looksLikeEmail(email)) {
      toast.error("That does not look like an email address.");
      return;
    }
    if (recipients.some((recipient) => recipient.email === email)) {
      toast.error(`${email} is already on the list.`);
      return;
    }
    if (recipients.length >= MAX_RECIPIENTS) {
      toast.error(`An RFQ can go to at most ${MAX_RECIPIENTS} yards.`);
      return;
    }
    setRecipients((current) => [...current, { email, name, company }]);
    form.reset();
    form.querySelector<HTMLInputElement>("input[name=email]")?.focus();
  }

  const ready = Boolean(runId);

  return (
    <>
      <Crumbs trail={[{ label: "RFQs", to: "/rfqs" }, { label: "New RFQ" }]} />

      <Container className="divide-y p-0">
        <SectionHeader
          level="h1"
          title="New RFQ"
          description="Send a finished takeoff to the yards you choose. Each gets a link to the material list and a form to quote it."
        />

        <div className="grid gap-5 px-6 py-5 sm:grid-cols-2">
          <Field
            label="Project"
            hint={
              projects && projects.projects.length === 0
                ? "No projects yet. Create one under Projects and upload its drawings first."
                : undefined
            }
          >
            {(control) => (
              <Select
                value={projectId}
                onValueChange={(value) => {
                  setChosenProject(value);
                  setChosenRun("");
                }}
              >
                <Select.Trigger id={control.id}>
                  <Select.Value placeholder="Choose a project" />
                </Select.Trigger>
                <Select.Content>
                  {projects?.projects.map((project) => (
                    <Select.Item key={project.id} value={project.id}>
                      {project.name}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select>
            )}
          </Field>
          <Field
            label="Takeoff"
            hint={
              projectId && detail && released.length === 0
                ? "This project has no finished takeoff yet. Upload drawings and wait for it to be ready."
                : "Only finished takeoffs can be sent. Yards see the list as it stands when you send."
            }
          >
            {(control) => (
              <Select value={runId} onValueChange={setChosenRun} disabled={!projectId}>
                <Select.Trigger id={control.id}>
                  <Select.Value placeholder="Choose a takeoff" />
                </Select.Trigger>
                <Select.Content>
                  {released.map((run) => (
                    <Select.Item key={run.id} value={run.id}>
                      {runLabel(run)}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select>
            )}
          </Field>
          {ready && projectId ? (
            <p className="txt-small text-ui-fg-subtle sm:col-span-2">
              <Link
                to="/projects/$id/takeoffs/$runId"
                params={{ id: projectId, runId }}
                className="text-ui-fg-interactive hover:text-ui-fg-interactive-hover"
              >
                Open this takeoff
              </Link>{" "}
              to check the lines before sending.
            </p>
          ) : null}
        </div>
      </Container>

      <Container className="divide-y p-0">
        <SectionHeader
          title="Message to the yards"
          description="Delivery date, site access, what to include. The same note goes to every yard."
        />
        <div className="px-6 py-5">
          <Field label="Message" optional>
            {(control) => (
              <Textarea
                {...control}
                rows={5}
                maxLength={5000}
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                placeholder="Delivery to the site by the 15th, curbside is fine. Please include delivery in your total and quote 2x6 studs as precut where you can."
              />
            )}
          </Field>
        </div>
      </Container>

      <Container className="divide-y p-0">
        <SectionHeader
          title="Yards"
          description={`Up to ${MAX_RECIPIENTS} yards. Each gets its own link, and sees only its own reply.`}
        />
        {recipients.length > 0 ? (
          <ul className="divide-y divide-ui-border-base">
            {recipients.map((recipient) => (
              <li key={recipient.email} className="flex items-center gap-x-3 px-6 py-3">
                <div className="min-w-0 flex-1">
                  <div className="txt-compact-small-plus truncate text-ui-fg-base">
                    {supplierName(recipient)}
                  </div>
                  <div className="txt-compact-xsmall truncate text-ui-fg-muted">
                    {supplierDetail(recipient)}
                  </div>
                </div>
                <IconButton
                  aria-label={`Remove ${recipient.email}`}
                  size="small"
                  onClick={() =>
                    setRecipients((current) =>
                      current.filter((candidate) => candidate.email !== recipient.email),
                    )
                  }
                >
                  <Trash />
                </IconButton>
              </li>
            ))}
          </ul>
        ) : null}
        <form
          onSubmit={addRecipient}
          className="grid gap-4 px-6 py-5 sm:grid-cols-[1.4fr_1fr_1fr_auto] sm:items-end"
        >
          <Field label="Email">
            {(control) => (
              <Input
                {...control}
                name="email"
                type="email"
                required
                autoComplete="off"
                placeholder="quotes@yard.com"
              />
            )}
          </Field>
          <Field label="Contact" optional>
            {(control) => <Input {...control} name="name" autoComplete="off" />}
          </Field>
          <Field label="Yard" optional>
            {(control) => <Input {...control} name="company" autoComplete="off" />}
          </Field>
          <Button
            type="submit"
            variant="secondary"
            disabled={recipients.length >= MAX_RECIPIENTS}
            className="max-sm:w-full"
          >
            <Plus /> Add yard
          </Button>
        </form>
        {recipients.length >= MAX_RECIPIENTS ? (
          <Hint className="px-6 py-3">That is the most yards one RFQ can go to.</Hint>
        ) : null}
      </Container>

      <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-end">
        <Button
          variant="secondary"
          disabled={!ready}
          isLoading={create.isPending && create.variables === false}
          onClick={() => create.mutate(false)}
          className="max-sm:w-full"
        >
          Create draft
        </Button>
        <Button
          disabled={!ready || recipients.length === 0}
          isLoading={create.isPending && create.variables === true}
          onClick={() => create.mutate(true)}
          className="max-sm:w-full"
        >
          <PaperPlane /> Create and send
        </Button>
      </div>
    </>
  );
}
